/**
 * 补消名单手动加入对话框（ADM-33 ADD_MEMBER，O-22 makeup-deduction）。
 *
 * 学生远程联想（studentsApi.listStudents，姓名/学号/手机号，300ms 防抖）+
 * 原因必填 ≤255 校验提示。409 业务拒绝（已在班/重复加入/课次不合格）透传
 * 后端中文文案；成功回调 onAdded 由父级刷新名单。
 * 动作级 RUM：action: makeup.addMember（attribute 白名单 sessionId/studentId）。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Select, Space, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import { studentsApi, type StudentListItem } from '../../api/students.api';
import { makeupDeductionApi } from '../../api/makeup-deduction.api';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { startAction } from '../../observability/otel';

const { Text } = Typography;

export interface MakeupAddMemberModalProps {
  open: boolean;
  /** 目标补消课次（空则不可提交）。 */
  sessionId: string | null;
  onClose: () => void;
  /** 加入成功回调（父级刷新补消名单）。 */
  onAdded: () => void;
}

/** 手动加入补消名单对话框。 */
export function MakeupAddMemberModal({
  open,
  sessionId,
  onClose,
  onAdded,
}: MakeupAddMemberModalProps) {
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [studentId, setStudentId] = useState<string | undefined>();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 关闭即清态（下次打开为干净表单）
  useEffect(() => {
    if (!open) {
      setStudents([]);
      setStudentId(undefined);
      setReason('');
      setReasonError(null);
    }
  }, [open]);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  // O-28：学生联想随全局校区（锁定模式下即批次所在校区；全部校区不带参）
  const { scope } = useGlobalCampusScope();
  const handleSearch = (keyword: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!keyword.trim()) {
      setStudents([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      void (async () => {
        setSearching(true);
        try {
          const result = await studentsApi.listStudents({
            keyword: keyword.trim(),
            // O-28：学生联想随全局校区（全部校区不带参）
            campus: scope === 'ALL' ? undefined : scope,
            page: 1,
            pageSize: 20,
          });
          setStudents(result.items);
        } catch {
          setStudents([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 300);
  };

  const options = useMemo(
    () =>
      students.map((item) => ({
        value: item.id,
        label: `${item.name}（${item.studentNo}）`,
      })),
    [students],
  );

  async function handleSubmit() {
    if (!sessionId) return;
    if (!studentId) {
      message.warning('请选择学生');
      return;
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('请填写加入原因');
      return;
    }
    if (trimmed.length > 255) {
      setReasonError('加入原因长度不得超过 255 字');
      return;
    }
    setSubmitting(true);
    const span = startAction('action: makeup.addMember', { sessionId, studentId });
    try {
      await makeupDeductionApi.addRosterMember(sessionId, studentId, trimmed);
      message.success('已加入补消名单');
      onAdded();
      onClose();
    } catch (error) {
      // 409 业务拒绝（已在班/重复加入/课次不合格）：后端中文文案直接提示
      message.error(error instanceof Error ? error.message : '手动加入失败');
    } finally {
      span.end();
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal
      title="手动加入补消名单"
      open={open}
      onCancel={onClose}
      width={520}
      destroyOnHidden
      data-testid="makeup-add-member-modal"
      footer={[
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={() => void handleSubmit()}
          data-testid="btn-confirm-add-member"
        >
          确认加入
        </Button>,
      ]}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* 手动加入表单：学生联想 + 原因必填（O-22） */}
        <Select
          showSearch
          filterOption={false}
          onSearch={handleSearch}
          loading={searching}
          notFoundContent="输入姓名/学号/手机号联想"
          placeholder="选择学生"
          aria-label="选择学生"
          style={{ width: '100%' }}
          value={studentId}
          options={options}
          onChange={(value) => setStudentId(value)}
          data-testid="add-member-student-select"
        />
        <Text type="secondary">
          可加入该课次时点不在班的学生（如课时耗尽被自动退班后续费的学生）；只绕过在班名单准入，消课仍按权益覆盖裁决。
        </Text>
        <div>
          <Input.TextArea
            rows={3}
            maxLength={255}
            showCount
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError(null);
            }}
            placeholder="请填写加入原因（必填，如：续费后补消历史课次）"
            aria-label="加入原因"
            status={reasonError ? 'error' : undefined}
            data-testid="add-member-reason-input"
          />
          {reasonError && (
            <Text type="danger" data-testid="add-member-reason-error">
              {reasonError}
            </Text>
          )}
        </div>
      </Space>
    </DraggableModal>
  );
}
